import { DeleteOutlined, InboxOutlined, ReloadOutlined, SaveOutlined, SendOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload';
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  Input,
  Space,
  Tag,
  Typography,
  Upload,
} from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ReportProjectWorkItemsSection } from '@/components/report/ReportProjectWorkItemsSection';
import {
  DELETE_DAILY_REPORT_AFTER_PR_SUCCESS_FAILURE,
  DELETE_DAILY_REPORT_FUSION_HINT,
  DELETE_DAILY_REPORT_FUSION_SUCCESS,
} from '@/constants/phase4-r-lite';
import {
  REPORT_IMAGE_MAX_COUNT,
  REPORT_IMAGE_MAX_SIZE_MB,
  REPORT_STATUS,
  REPORT_STATUS_LABELS,
  REPORT_SUBMIT_DEADLINE_HOUR,
} from '@/constants/report';
import { useManagedProjectsForReport } from '@/hooks/useManagedProjectsForReport';
import { ApiError } from '@/services/api/client';
import {
  buildBundleSortOrderMap,
  buildProjectNameLookup,
  deleteDailyReportWithFusion,
  fetchTodayProjectReportsForUser,
  formatProjectReportDeleteFailureMessage,
  formatProjectWorkItemFailureLabel,
  mapProjectReportsToWorkItems,
  retryFailedProjectWorkItems,
  submitDailyReportWithProjectItems,
  type ProjectWorkItemSubmitFailure,
} from '@/services/report-daily-fusion';
import * as reportsApi from '@/services/api/reports';
import * as uploadsApi from '@/services/api/uploads';
import { useAuthStore } from '@/stores/auth.store';
import type { ProjectWorkItemFormValue } from '@/types/report-project-work-item-lite';
import type { ReportRecord, UploadedReportImage } from '@/types/report';
import { getLocalDateKey, isAfterDeadlineHour } from '@/utils/date';
import { readReportLiteBundle } from '@/utils/report-lite-bundle-store';
import { validateProjectWorkItems } from '@/utils/report-project-work-item-lite';

interface ReportFormValues {
  todayWork: string;
  tomorrowPlan?: string;
  problemRemark?: string;
}

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

interface ReportSubmitPageProps {
  variant?: 'desktop' | 'mobile' | 'mobile-view';
}

export function ReportSubmitPage({ variant = 'desktop' }: ReportSubmitPageProps) {
  const isMobile = variant === 'mobile' || variant === 'mobile-view';
  const viewOnly = variant === 'mobile-view';
  const { message, modal } = App.useApp();
  const [form] = Form.useForm<ReportFormValues>();
  const user = useAuthStore((state) => state.user);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [todayReport, setTodayReport] = useState<ReportRecord | null>(null);
  const [images, setImages] = useState<UploadedReportImage[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [projectWorkItems, setProjectWorkItems] = useState<ProjectWorkItemFormValue[]>([]);
  const [baselineProjectWorkItems, setBaselineProjectWorkItems] = useState<ProjectWorkItemFormValue[]>(
    [],
  );
  const [projectSubmitFailures, setProjectSubmitFailures] = useState<ProjectWorkItemSubmitFailure[]>(
    [],
  );
  const [retryingProjectItems, setRetryingProjectItems] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const {
    managedProjects,
    loading: loadingManagedProjects,
    error: managedProjectsError,
    showProjectWorkItemsSection,
  } = useManagedProjectsForReport();

  const dateKey = useMemo(() => getLocalDateKey(), []);
  const pastDeadline = isAfterDeadlineHour(REPORT_SUBMIT_DEADLINE_HOUR);
  const isDraft = todayReport?.status === REPORT_STATUS.DRAFT;
  const isSubmitted = Boolean(todayReport && !isDraft);

  const loadProjectWorkItemsForToday = useCallback(
    async (report: ReportRecord | null) => {
      if (!user || managedProjects.length === 0) {
        setProjectWorkItems([]);
        setBaselineProjectWorkItems([]);
        return;
      }

      try {
        const records = await fetchTodayProjectReportsForUser(user.id, dateKey);
        const bundle = report ? readReportLiteBundle(user.id, report.id) : null;
        const sortMap = bundle ? buildBundleSortOrderMap(bundle.items) : undefined;
        const items = mapProjectReportsToWorkItems(records, dateKey, user.id, sortMap);
        setProjectWorkItems(items);
        setBaselineProjectWorkItems(items.map((item) => ({ ...item })));
        setProjectSubmitFailures([]);
      } catch (error) {
        const text = error instanceof ApiError ? error.message : '加载项目进展失败';
        message.error(text);
      }
    },
    [dateKey, managedProjects.length, message, user],
  );

  const loadTodayReport = useCallback(async () => {
    if (!user) {
      return;
    }

    setLoading(true);
    try {
      const result = await reportsApi.fetchReports({
        page: 1,
        pageSize: 1,
        userId: user.id,
        startDate: dateKey,
        endDate: dateKey,
      });

      const report = result.items[0] ?? null;
      setTodayReport(report);

      if (report) {
        form.setFieldsValue({
          todayWork: report.todayWork,
          tomorrowPlan: report.tomorrowPlan ?? undefined,
          problemRemark: report.problemRemark ?? undefined,
        });

        const loadedImages: UploadedReportImage[] = [];
        const urls = await Promise.all(
          report.images.map(async (image) => {
            try {
              const { url } = await uploadsApi.fetchReportImageAccessUrl(image.id);
              return {
                id: image.id,
                originalName: image.originalName,
                mimeType: image.mimeType,
                size: image.size,
                previewUrl: url,
              } satisfies UploadedReportImage;
            } catch {
              return {
                id: image.id,
                originalName: image.originalName,
                mimeType: image.mimeType,
                size: image.size,
              } satisfies UploadedReportImage;
            }
          }),
        );
        loadedImages.push(...urls);
        setImages(loadedImages);
        setFileList(
          loadedImages.map((image) => ({
            uid: image.id,
            name: image.originalName,
            status: 'done',
            url: image.previewUrl,
          })),
        );
      } else {
        form.resetFields();
        setImages([]);
        setFileList([]);
      }
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载今日日报失败';
      message.error(text);
    } finally {
      setLoading(false);
    }
  }, [dateKey, form, message, user]);

  useEffect(() => {
    void loadTodayReport();
  }, [loadTodayReport]);

  useEffect(() => {
    if (!user || loadingManagedProjects) {
      return;
    }
    if (managedProjects.length === 0) {
      setProjectWorkItems([]);
      setBaselineProjectWorkItems([]);
      return;
    }
    void loadProjectWorkItemsForToday(todayReport);
  }, [
    loadProjectWorkItemsForToday,
    loadingManagedProjects,
    managedProjects.length,
    todayReport?.id,
    user,
  ]);

  const buildPayload = (values: ReportFormValues) => ({
    todayWork: values.todayWork.trim(),
    tomorrowPlan: values.tomorrowPlan?.trim() || undefined,
    problemRemark: values.problemRemark?.trim() || undefined,
    imageIds: images.map((image) => image.id),
  });

  const handleSaveDraft = async () => {
    if (isSubmitted) {
      message.info('今日日报已提交，请使用「保存修改」');
      return;
    }

    const values = form.getFieldsValue();
    setSavingDraft(true);
    try {
      await reportsApi.saveReportDraft({
        todayWork: values.todayWork ?? '',
        tomorrowPlan: values.tomorrowPlan,
        problemRemark: values.problemRemark,
        imageIds: images.map((image) => image.id),
      });
      message.success('草稿已保存');
      await loadTodayReport();
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '保存草稿失败';
      message.error(text);
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmit = async () => {
    if (pastDeadline && !isSubmitted) {
      message.warning(`已超过当日 ${REPORT_SUBMIT_DEADLINE_HOUR}:00 提交截止时间`);
      return;
    }

    if (projectWorkItems.length > 0) {
      const validationError = validateProjectWorkItems(projectWorkItems);
      if (validationError) {
        message.warning(validationError);
        return;
      }
    }

    const values = await form.validateFields();
    const payload = buildPayload(values);

    if (!user) {
      return;
    }

    setSubmitting(true);
    setProjectSubmitFailures([]);
    try {
      const result = await submitDailyReportWithProjectItems({
        userId: user.id,
        reportDate: dateKey,
        reportBody: payload,
        projectWorkItems,
        existingReportId: todayReport?.id,
        isUpdate: isSubmitted,
        baselineProjectWorkItems: isSubmitted ? baselineProjectWorkItems : [],
      });

      setTodayReport(result.report);
      setProjectWorkItems(result.projectWorkItems);
      setBaselineProjectWorkItems(result.projectWorkItems.map((item) => ({ ...item })));
      setProjectSubmitFailures(result.failures);

      if (result.partialFailure) {
        message.warning('日报已保存，但部分项目进展未能同步');
      } else if (isSubmitted) {
        message.success('日报已更新');
      } else {
        message.success('日报已提交');
      }

      if (managedProjects.length > 0 && !result.partialFailure) {
        await loadProjectWorkItemsForToday(result.report);
      }

      form.setFieldsValue({
        todayWork: result.report.todayWork,
        tomorrowPlan: result.report.tomorrowPlan ?? undefined,
        problemRemark: result.report.problemRemark ?? undefined,
      });
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '提交失败';
      message.error(text);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetryFailedProjectItems = async () => {
    if (!user || !todayReport || projectSubmitFailures.length === 0) {
      return;
    }

    const failedEntries = projectSubmitFailures;
    if (failedEntries.length === 0) {
      return;
    }

    setRetryingProjectItems(true);
    try {
      const result = await retryFailedProjectWorkItems({
        userId: user.id,
        reportId: todayReport.id,
        reportDate: dateKey,
        allItems: projectWorkItems,
        failures: failedEntries,
      });

      setProjectWorkItems(result.projectWorkItems);
      setBaselineProjectWorkItems(result.projectWorkItems.map((item) => ({ ...item })));
      setProjectSubmitFailures(result.failures);

      if (result.partialFailure) {
        message.warning('仍有项目进展未能同步，请再次重试');
      } else {
        message.success('项目进展已同步');
        await loadProjectWorkItemsForToday(todayReport);
      }
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '重试失败';
      message.error(text);
    } finally {
      setRetryingProjectItems(false);
    }
  };

  const handleDeleteReport = () => {
    if (!user || !todayReport) {
      return;
    }

    modal.confirm({
      title: '确认删除日报',
      content: (
        <div>
          <Typography.Paragraph style={{ marginBottom: 8 }}>
            确定删除 {dateKey} 的日报吗？此操作不可恢复。
          </Typography.Paragraph>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {DELETE_DAILY_REPORT_FUSION_HINT}
          </Typography.Paragraph>
        </div>
      ),
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        setDeleting(true);
        try {
          const nameLookup = buildProjectNameLookup(projectWorkItems, managedProjects);
          const result = await deleteDailyReportWithFusion(
            user.id,
            todayReport.id,
            todayReport.reportDate,
            nameLookup,
            {
              reportSubmittedAt: todayReport.submittedAt,
              reportCreatedAt: todayReport.createdAt,
            },
          );

          if (result.projectReportFailures.length > 0) {
            message.error(formatProjectReportDeleteFailureMessage(result.projectReportFailures));
            return;
          }

          if (result.reportDeleteFailed) {
            message.error(
              result.reportDeleteErrorMessage ?? DELETE_DAILY_REPORT_AFTER_PR_SUCCESS_FAILURE,
            );
            await loadProjectWorkItemsForToday(todayReport);
            return;
          }

          if (result.fusionFallbackWarning) {
            message.warning(result.fusionFallbackWarning);
          }
          message.success(DELETE_DAILY_REPORT_FUSION_SUCCESS);
          setTodayReport(null);
          setProjectWorkItems([]);
          setBaselineProjectWorkItems([]);
          setProjectSubmitFailures([]);
          form.resetFields();
          setImages([]);
          setFileList([]);
          await loadTodayReport();
        } catch (error) {
          const text = error instanceof ApiError ? error.message : '删除失败';
          message.error(text);
          throw error;
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  const handleUpload = async (file: File) => {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      message.error('仅支持 JPEG、PNG、WebP 图片');
      return Upload.LIST_IGNORE;
    }
    if (file.size > REPORT_IMAGE_MAX_SIZE_MB * 1024 * 1024) {
      message.error(`单张图片不能超过 ${REPORT_IMAGE_MAX_SIZE_MB}MB`);
      return Upload.LIST_IGNORE;
    }
    if (images.length >= REPORT_IMAGE_MAX_COUNT) {
      message.error(`最多上传 ${REPORT_IMAGE_MAX_COUNT} 张图片`);
      return Upload.LIST_IGNORE;
    }

    setUploading(true);
    try {
      const uploaded = await uploadsApi.uploadReportImage(file);
      setImages((prev) => [...prev, uploaded]);
      setFileList((prev) => [
        ...prev,
        {
          uid: uploaded.id,
          name: uploaded.originalName,
          status: 'done',
          url: uploaded.previewUrl,
        },
      ]);
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '图片上传失败';
      message.error(text);
      return Upload.LIST_IGNORE;
    } finally {
      setUploading(false);
    }

    return false;
  };

  const handleRemoveImage = (file: UploadFile) => {
    setImages((prev) => prev.filter((image) => image.id !== file.uid));
    setFileList((prev) => prev.filter((item) => item.uid !== file.uid));
    return true;
  };

  if (!user) {
    return null;
  }

  return (
    <div>
      {!isMobile ? (
        <>
          <Typography.Title level={3} style={{ marginTop: 0 }}>
            今日日报
          </Typography.Title>
          <Typography.Paragraph type="secondary">
            {dateKey} · 请在当日 {REPORT_SUBMIT_DEADLINE_HOUR}:00 前提交
          </Typography.Paragraph>
        </>
      ) : (
        <p className="mobile-card__meta" style={{ margin: '0 0 12px' }}>
          {dateKey} · 请在当日 {REPORT_SUBMIT_DEADLINE_HOUR}:00 前提交
        </p>
      )}

      {pastDeadline && !isSubmitted ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={`已过提交截止时间（${REPORT_SUBMIT_DEADLINE_HOUR}:00），仅可查看或尝试修改已提交的日报`}
        />
      ) : null}

      {todayReport ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <Space>
              <span>{isDraft ? '今日日报草稿' : '今日日报已提交'}</span>
              <Tag>{REPORT_STATUS_LABELS[todayReport.status]}</Tag>
            </Space>
          }
        />
      ) : null}

      {managedProjectsError ? (
        <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={managedProjectsError} />
      ) : null}

      {projectSubmitFailures.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="以下项目保存失败："
          description={
            <div>
              <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                {projectSubmitFailures.map((entry) => (
                  <li key={`${entry.operation}-${entry.item.clientKey}-${entry.item.projectReportId ?? 'new'}`}>
                    {formatProjectWorkItemFailureLabel(entry)}
                  </li>
                ))}
              </ul>
              <Button
                size="small"
                icon={<ReloadOutlined />}
                loading={retryingProjectItems}
                onClick={() => void handleRetryFailedProjectItems()}
              >
                重试保存失败项
              </Button>
            </div>
          }
        />
      ) : null}

      <Card loading={loading} bordered={!isMobile} className={isMobile ? 'mobile-form-card' : undefined}>
        <Form<ReportFormValues> form={form} layout="vertical" disabled={loading || viewOnly}>
          <Form.Item
            name="todayWork"
            label="今日工作"
            rules={[{ required: true, message: '请填写今日工作' }]}
          >
            <Input.TextArea rows={6} placeholder="请描述今日完成的主要工作" maxLength={10000} showCount />
          </Form.Item>
          <Form.Item name="tomorrowPlan" label="明日计划">
            <Input.TextArea rows={4} placeholder="可选" maxLength={10000} showCount />
          </Form.Item>
          <Form.Item name="problemRemark" label="问题与风险">
            <Input.TextArea rows={4} placeholder="可选，填写遇到的问题与风险" maxLength={10000} showCount />
          </Form.Item>
          <Form.Item label={`图片（最多 ${REPORT_IMAGE_MAX_COUNT} 张）`}>
            <Upload.Dragger
              multiple
              listType="picture"
              fileList={fileList}
              beforeUpload={async (file) => handleUpload(file as File)}
              onRemove={handleRemoveImage}
              disabled={viewOnly || uploading || (pastDeadline && !isSubmitted)}
              accept={ALLOWED_IMAGE_TYPES.join(',')}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽上传图片</p>
              <p className="ant-upload-hint">
                支持 JPEG/PNG/WebP，单张不超过 {REPORT_IMAGE_MAX_SIZE_MB}MB
              </p>
            </Upload.Dragger>
          </Form.Item>

          {showProjectWorkItemsSection ? (
            <ReportProjectWorkItemsSection
              isMobile={isMobile}
              disabled={loading || viewOnly || (pastDeadline && !isSubmitted && !isDraft)}
              managedProjects={managedProjects}
              loadingProjects={loadingManagedProjects}
              items={projectWorkItems}
              onChange={setProjectWorkItems}
            />
          ) : null}

          <Form.Item>
            {viewOnly ? null : isMobile ? (
              <div className="mobile-form-actions">
                {!isSubmitted ? (
                  <button
                    type="button"
                    className="mobile-btn mobile-btn--ghost mobile-btn--block"
                    onClick={() => void handleSaveDraft()}
                    disabled={loading || savingDraft || deleting || (pastDeadline && !isDraft)}
                  >
                    {savingDraft ? '保存中…' : '保存草稿'}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="mobile-btn mobile-btn--primary mobile-btn--block"
                  onClick={() => void handleSubmit()}
                  disabled={
                    loading || submitting || deleting || (pastDeadline && !isSubmitted && !isDraft)
                  }
                >
                  {submitting ? '提交中…' : isSubmitted ? '保存修改' : '提交日报'}
                </button>
                {todayReport ? (
                  <button
                    type="button"
                    className="mobile-btn mobile-btn--danger mobile-btn--block"
                    onClick={handleDeleteReport}
                    disabled={loading || submitting || savingDraft || deleting}
                  >
                    {deleting ? '删除中…' : '删除日报'}
                  </button>
                ) : null}
              </div>
            ) : (
              <Space wrap>
                {!isSubmitted ? (
                  <Button
                    icon={<SaveOutlined />}
                    onClick={() => void handleSaveDraft()}
                    loading={savingDraft}
                    disabled={loading || deleting || (pastDeadline && !isDraft)}
                  >
                    保存草稿
                  </Button>
                ) : null}
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={() => void handleSubmit()}
                  loading={submitting}
                  disabled={loading || deleting || (pastDeadline && !isSubmitted && !isDraft)}
                >
                  {isSubmitted ? '保存修改' : '提交日报'}
                </Button>
                {todayReport ? (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={handleDeleteReport}
                    loading={deleting}
                    disabled={loading || submitting || savingDraft}
                  >
                    删除日报
                  </Button>
                ) : null}
              </Space>
            )}
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
